import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ArrowUpRight } from 'lucide-react';
import { formatPrice } from '../components/PropertyCard';
import { PROPERTY } from '../constants/testIds';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PropertyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(0);

  useEffect(() => {
    axios.get(`${API}/properties/${id}`)
      .then((r) => setProperty(r.data))
      .catch(() => setProperty(null))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="px-6 md:px-24 py-32 text-center text-[11px] tracking-[0.3em] uppercase text-[#A3A3A3]">Loading dossier…</div>;
  if (!property) return (
    <div className="px-6 md:px-24 py-32 text-center">
      <div className="text-[11px] tracking-[0.3em] uppercase text-[#D4AF37] mb-4">404</div>
      <p className="text-2xl font-light mb-8">This address is sealed.</p>
      <Link to="/vault" className="btn-sovereign">Return to Vault</Link>
    </div>
  );

  return (
    <div>
      <section className="relative h-[75vh] overflow-hidden">
        <img src={property.images[activeImage]} alt={property.title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-[#050505]/40" />
        <button
          onClick={() => navigate('/vault')}
          data-testid={PROPERTY.back}
          className="absolute top-8 left-6 md:left-12 lg:left-24 flex items-center gap-3 text-[11px] tracking-[0.3em] uppercase text-[#D4AF37] hover:text-white transition-colors"
        >
          <ArrowLeft size={16} strokeWidth={1.5} /> Vault
        </button>
        <div className="absolute bottom-0 left-0 right-0 px-6 md:px-12 lg:px-24 pb-16">
          <div className="max-w-5xl">
            <div className="flex items-center gap-3 mb-6">
              <span className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37] border border-[#D4AF37]/40 px-3 py-1 bg-black/40 backdrop-blur-sm">
                {property.listing_type}
              </span>
              <span className="text-[10px] tracking-[0.3em] uppercase text-[#F5F5F5]">
                {property.region}
              </span>
            </div>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-extralight leading-[1] tracking-tight">{property.title}</h1>
          </div>
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-24 py-6 grid grid-cols-3 md:grid-cols-6 gap-3">
        {property.images.map((img, i) => (
          <button
            key={i}
            onClick={() => setActiveImage(i)}
            className={`aspect-[4/3] overflow-hidden transition-all ${activeImage === i ? 'opacity-100 ring-1 ring-[#D4AF37]' : 'opacity-50 hover:opacity-100'}`}
          >
            <img src={img} alt="" className="w-full h-full object-cover" />
          </button>
        ))}
      </section>

      <section className="px-6 md:px-12 lg:px-24 py-16 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
        <div className="md:col-span-7">
          <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-6">Dossier</div>
          <p className="text-lg md:text-xl font-light leading-relaxed text-[#F5F5F5] mb-12">
            {property.description}
          </p>

          <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-6">Features</div>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 mb-12">
            {property.features.map((f, i) => (
              <li key={i} className="text-sm font-light text-[#F5F5F5] flex items-start gap-3 border-b border-[#D4AF37]/10 pb-3">
                <span className="w-1 h-1 bg-[#D4AF37] mt-2 flex-shrink-0"></span>
                {f}
              </li>
            ))}
          </ul>
        </div>

        <aside className="md:col-span-5">
          <div className="sticky top-28 sov-border p-8 md:p-10 bg-[#0A0A0A]">
            <div className="text-[10px] tracking-[0.4em] uppercase text-[#A3A3A3] mb-3">Asking</div>
            <div className="font-display text-5xl md:text-6xl text-[#D4AF37] mb-8">{formatPrice(property.price_usd)}</div>

            <div className="grid grid-cols-3 gap-4 mb-8 pb-8 border-b border-[#D4AF37]/15">
              <div>
                <div className="text-[9px] tracking-[0.3em] uppercase text-[#A3A3A3] mb-2">Bed</div>
                <div className="font-display text-2xl">{property.bedrooms}</div>
              </div>
              <div>
                <div className="text-[9px] tracking-[0.3em] uppercase text-[#A3A3A3] mb-2">Bath</div>
                <div className="font-display text-2xl">{property.bathrooms}</div>
              </div>
              <div>
                <div className="text-[9px] tracking-[0.3em] uppercase text-[#A3A3A3] mb-2">Sqft</div>
                <div className="font-display text-2xl">{(property.area_sqft / 1000).toFixed(1)}k</div>
              </div>
            </div>

            {property.yield_estimate && (
              <div className="flex items-center justify-between pb-6 mb-6 border-b border-[#D4AF37]/15">
                <span className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3]">Yield Estimate</span>
                <span className="font-display text-2xl text-[#D4AF37]">{property.yield_estimate}%</span>
              </div>
            )}

            <div className="flex items-center justify-between pb-6 mb-6 border-b border-[#D4AF37]/15">
              <span className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3]">Status</span>
              <span className="text-sm tracking-wide text-[#D4AF37]">{property.status}</span>
            </div>

            <Link to="/boarding" data-testid={PROPERTY.inquire} className="btn-sovereign w-full">
              Request Private Showing <ArrowUpRight size={16} strokeWidth={1.5} />
            </Link>
          </div>
        </aside>
      </section>
    </div>
  );
}
